import { motion, useInView, useReducedMotion } from 'motion/react';
import {
  type ForwardedRef,
  type ReactNode,
  createElement,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { cn } from '../lib/cn';

type InkedTitleProps = {
  children: ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'span';
  className?: string;
  delay?: number;
  duration?: number;
  /** When true plays on mount, else on inView */
  immediate?: boolean;
};

/**
 * Editorial mask reveal: text wrapper sits in flow; inner span starts at
 * translateY 105% and rises into view. Observer is attached to the wrapper
 * (in-flow), not the translated span, otherwise IO never fires.
 *
 * The wrapper ref is forwarded so callers (e.g. useMagnetic) can hook into it.
 */
export const InkedTitle = forwardRef<HTMLElement, InkedTitleProps>(function InkedTitle(
  { children, as = 'h1', className, delay = 0, duration = 1.1, immediate = false },
  forwardedRef: ForwardedRef<HTMLElement>,
) {
  const reduce = useReducedMotion();
  const localRef = useRef<HTMLElement | null>(null);
  const inView = useInView(localRef, { once: true, amount: 0.35 });

  useImperativeHandle(forwardedRef, () => localRef.current as HTMLElement, []);

  if (reduce) {
    return createElement(
      as,
      { ref: localRef, className: cn('block', className) },
      children,
    );
  }

  const shouldPlay = immediate || inView;

  return createElement(
    as,
    { ref: localRef, className: cn('block overflow-hidden', className) },
    <motion.span
      className="block"
      initial={{ y: '105%' }}
      animate={shouldPlay ? { y: '0%' } : { y: '105%' }}
      transition={{ duration, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>,
  );
});
